import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, ChapterTag, MaskedLine } from "@/components/Reveal";

const services = [
  { title: "AI Strategy & Consulting", desc: "Cut through the AI noise. Evaluate where artificial intelligence genuinely creates value in your business — then build a practical, staged roadmap to get there.", tags: ["ASSESSMENT", "ROADMAP", "ROI"] },
  { title: "Custom AI Solutions & Generative AI", desc: "Custom AI applications, generative AI integrations, and multimodal systems designed around your workflows, your data, and your customers.", tags: ["GENAI", "MULTIMODAL", "CUSTOM AI"] },
  { title: "AI Agents & Automation", desc: "Intelligent agents and automation pipelines that remove repetitive work, connect your existing systems, and operate around the clock.", tags: ["AGENTS", "WORKFLOWS", "INTEGRATION"] },
  { title: "Computer Vision", desc: "Camera-based systems that see, detect, count, inspect, and alert — from devices in the field to live dashboards and automated action.", tags: ["DETECTION", "INSPECTION", "ALERTS"] },
  { title: "Smart Glass & Wearable Development", desc: "Wearable application development since the original Google Glass release — now applied to today's generation of AI-powered smart glasses, voice, and camera interfaces.", tags: ["SMART GLASSES", "VOICE", "WEARABLES"] },
  { title: "IoT & Asset Tracking", desc: "GPS, cellular, Bluetooth/BLE, Wi-Fi, RFID, QR, and sensor-based tracking systems — from the physical device in the field to the live map on screen.", tags: ["GPS", "CELLULAR", "BLE", "RFID"] },
  { title: "Hardware + Software Integration", desc: "The bridge between physical technology and digital systems — embedded devices, edge computing, connectivity, cloud platforms, and applications working as one.", tags: ["EMBEDDED", "EDGE", "CLOUD"] },
  { title: "Cloud Platforms, APIs & Dashboards", desc: "The cloud infrastructure, APIs, databases, and real-time dashboards that turn raw device and business data into decisions.", tags: ["CLOUD", "APIS", "DATA"] },
  { title: "Rapid Prototyping & Proof-of-Concept", desc: "Determine whether an idea works in weeks instead of quarters — proof-of-concept development before large investments are made.", tags: ["POC", "VALIDATION", "SPEED"] },
  { title: "AI Training & Enablement", desc: "Practical AI training for owners, executives, and teams — working knowledge of what AI can do today and how to use it responsibly.", tags: ["WORKSHOPS", "TEAMS", "ENABLEMENT"] },
];

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-20">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 md:pb-24">
        <ChapterTag num="03" label="Services" />
        <MaskedLine className="mt-12 font-unbounded font-extrabold uppercase tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-7xl">
          Solutions
        </MaskedLine>
        <MaskedLine delay={0.12} className="font-unbounded font-extrabold uppercase tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-7xl text-outline">
          End to end.
        </MaskedLine>
        <Reveal delay={0.25}>
          <p className="mt-10 max-w-2xl text-base md:text-lg text-neutral-400 leading-relaxed">
            From strategy to working systems — every engagement is built around a practical business outcome, not technology for its own sake.
          </p>
        </Reveal>
      </section>

      <section data-testid="services-list" className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={Math.min(i * 0.04, 0.2)}>
              <div
                data-testid={`service-row-${i}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-10 md:py-12 border-b border-white/10 hover:bg-white/[0.02] transition-colors duration-500 md:px-4 -mx-0 md:-mx-4"
              >
                <div className="md:col-span-1">
                  <span className="font-mono text-sm text-cyan/70">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="md:col-span-4">
                  <h2 className="font-unbounded font-normal uppercase tracking-tight text-lg md:text-xl text-white group-hover:text-cyan transition-colors duration-300 leading-snug">
                    {s.title}
                  </h2>
                </div>
                <div className="md:col-span-5">
                  <p className="text-sm md:text-base text-neutral-400 leading-relaxed">{s.desc}</p>
                </div>
                <div className="md:col-span-2 flex md:flex-col md:items-end justify-between gap-3">
                  <div className="flex flex-wrap md:justify-end gap-2">
                    {s.tags.map((t) => (
                      <span key={t} className="font-mono text-[9px] tracking-[0.2em] text-neutral-500 border border-white/10 px-2 py-1 h-fit">
                        {t}
                      </span>
                    ))}
                  </div>
                  <ArrowRight size={18} className="text-neutral-600 group-hover:text-cyan group-hover:translate-x-1 transition-all duration-300 hidden md:block" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="services-cta" className="bg-blueprint border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32 flex flex-col md:flex-row md:items-end justify-between gap-10">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em] text-cyan mb-6">// NOT SURE WHERE TO START?</p>
            <h2 className="font-unbounded font-semibold uppercase tracking-tight text-2xl sm:text-4xl leading-[1.1] max-w-xl">
              A short conversation is the fastest way to find out what's possible.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="services-cta-talk"
            className="group inline-flex w-fit items-center gap-3 bg-cyan text-black px-8 py-4 font-mono text-xs tracking-[0.25em] font-medium hover:bg-cyan-hover transition-colors duration-300"
          >
            LET'S TALK
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </main>
  );
}
